/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { Either, EitherAsync, Maybe } from 'purify-ts';
import { getStoreBuilder } from 'vuex-typex';
import * as compression from '@codegrade/compression';
import * as deque from '@codegrade/deque';
import { eitherAsync, map } from '@codegrade/utils';
import * as utils from '@/utils';
import * as fileUtils from '@/utils/files';
import * as signals from '@/utils/signal';
/**
 * By default the max size of 16MiB.
 *
 * This is stored in a mutable object to reduce this size during tests.
 */
export const MAX_CACHE_SIZE = { value: 1 << 24 };
const storeBuilder = getStoreBuilder();
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function makeCacheKey(route, hash) {
    return hash.map(h => `H:${h}`).orDefaultLazy(() => `R:${utils.buildUrl(route)}`);
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function getClearState() {
    return {
        cacheMap: new map.MaybeMap(),
        cacheDeque: new deque.Deque(),
        cacheSize: 0,
        loaders: {},
    };
}
const moduleBuilder = storeBuilder.module('code', getClearState());
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export var CodeStore;
(function (CodeStore) {
    const clearCodeCache = moduleBuilder.commit(state => {
        const cleanState = getClearState();
        Object.entries(cleanState).forEach(([key, value]) => {
            utils.vueSet(state, key, value);
        });
    }, 'clearCodeCache');
    signals.AFTER_USER_CHANGED.register(async () => {
        clearCodeCache();
    });
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    const accessedCode = moduleBuilder.commit((state, { key }) => {
        const deq = state.cacheDeque;
        if (deq.isEmpty()) {
            return;
        }
        const lastVal = deq.peekFront();
        if (lastVal === key) {
            return;
        }
        for (let len = deq.length, oldVal = lastVal, i = 1; i < len; ++i) {
            const prevVal = deq.setAt(i, oldVal);
            if (prevVal === key) {
                // We just overridden the value we want at the beginning, so we
                // set it at index 0 and we are done.
                deq.setAt(0, prevVal);
                break;
            }
            else {
                oldVal = prevVal;
            }
        }
    }, 'accessedCode');
    const addCodeToCache = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, { key, compressed }) => {
        const maxSize = MAX_CACHE_SIZE.value;
        const deq = state.cacheDeque;
        const cacheMap = state.cacheMap;
        if (compressed.compressedByteLength > maxSize) {
            return;
        }
        let cacheSize = state.cacheSize + compressed.compressedByteLength;
        const removedIds = [];
        while (cacheSize > maxSize && !deq.isEmpty()) {
            const toDeleteId = deq.pop();
            const toDelete = cacheMap.get(toDeleteId).unsafeCoerce();
            if (toDelete != null) {
                cacheSize -= toDelete.compressedByteLength;
                removedIds.push(toDeleteId);
            }
        }
        for (let i = removedIds.length - 1; i >= 0; --i) {
            const removedId = removedIds[i];
            const item = cacheMap.get(removedId).unsafeCoerce();
            if (cacheSize + item.compressedByteLength < maxSize) {
                deq.push(removedId);
                cacheSize += item.compressedByteLength;
            }
            else {
                state.cacheMap.delete(removedId);
            }
        }
        deq.unshift(key);
        cacheMap.set(key, compressed);
        state.cacheSize = cacheSize;
    }, 'addCodeToCache');
    const setLoader = moduleBuilder.commit((state, data) => {
        if (data.op === 'delete') {
            utils.vueDelete(state.loaders, data.key);
        }
        else {
            utils.vueSet(state.loaders, data.key, data.value);
        }
    }, 'setLoader');
    const getOrDownload = moduleBuilder.dispatch((ctx, { loader, cacheKey, }) => ctx.state.cacheMap.get(cacheKey).caseOf({
        Just: async (cached) => {
            accessedCode({ key: cacheKey });
            return await eitherAsync.toPromise(cached.decompress());
        },
        Nothing: () => {
            if (cacheKey in ctx.state.loaders) {
                return ctx.state.loaders[cacheKey];
            }
            let promise;
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            const clearLoader = () => {
                promise = undefined;
                setLoader({ op: 'delete', key: cacheKey });
            };
            promise = eitherAsync.toPromise(loader
                .chain(data => compression
                .compress(data)
                .ifRight(compressed => {
                addCodeToCache({
                    key: cacheKey,
                    compressed,
                });
            })
                .chainLeft(() => utils.eitherAsyncUtils.rightFromValue(data))
                .map(() => data))
                .finally(() => {
                clearLoader();
            }));
            setLoader({ op: 'set', key: cacheKey, value: promise });
            return promise;
        },
    }), 'getOrDownload');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    function loadCodeFromRoute({ route, hash = Maybe.empty(), }) {
        const cacheKey = makeCacheKey(route, hash);
        const fullUrl = utils.buildUrl(route, { query: { as_url: true } });
        return getOrDownload({
            cacheKey,
            loader: fileUtils.downloadRawFile(fullUrl).map(buf => new Uint8Array(buf)),
        });
    }
    CodeStore.loadCodeFromRoute = loadCodeFromRoute;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    function loadCode(payload) {
        if (payload.file == null) {
            return loadCodeFromRoute({
                route: ['api', 'v1', 'code', payload.fileId],
            });
        }
        else {
            const file = payload.file;
            return loadCodeFromRoute({
                route: ['api', 'v1', 'code', file.id],
                hash: file.hash,
            });
        }
    }
    CodeStore.loadCode = loadCode;
    /**
     * Download a file and decompress it from a signed S3 link.
     *
     * Compression detection is done as described in the @codegrade/compression module.
     *
     * @param url The URL to the file.
     * @returns The decompressed downloaded file.
     */
    function downloadCompressedFileFromS3(url) {
        return EitherAsync(() => getOrDownload({
            cacheKey: makeCacheKey(url, Maybe.empty()),
            loader: fileUtils.downloadCompressedFileFromS3(url),
        }));
    }
    CodeStore.downloadCompressedFileFromS3 = downloadCompressedFileFromS3;
    /**
     * Download, decompress, and decode a JSON file from a signed S3 link.
     *
     * Compression detection is done as described in the @codegrade/compression module.
     *
     * @param url The URL to JSON.
     * @param parser The parser describing the format of the JSON.
     * @returns The decoded JSON.
     */
    function downloadCompressedJSONFromS3(url, parser) {
        return downloadCompressedFileFromS3(url).chain(async (response) => Either.encase(() => JSON.parse(new TextDecoder('utf8', { fatal: true }).decode(response))).chain(json => parser.decode(json).mapLeft(Error)));
    }
    CodeStore.downloadCompressedJSONFromS3 = downloadCompressedJSONFromS3;
})(CodeStore || (CodeStore = {}));
